import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/Layout";

const field = "w-full border border-neutral-200 px-3 py-3 text-sm focus:border-neutral-900 outline-none";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8"><Logo /></div>
        <h1 className="font-display text-4xl font-light text-center">{title}</h1>
        {sub && <p className="text-sm text-neutral-500 text-center mt-2">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

const roleHome = (u) => (u.role === "admin" ? "/admin" : u.role === "seller" ? "/espace-createur" : "/compte");

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await login(f.email, f.password); toast.success("Bienvenue"); nav(loc.state?.from || roleHome(u)); }
    catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };
  return (
    <Shell title="Connexion" sub="Accédez à votre espace Cagole Chic">
      <form onSubmit={submit} className="space-y-4">
        <input type="email" required placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="login-email" />
        <input type="password" required placeholder="Mot de passe" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={field} data-testid="login-password" />
        <div className="text-right"><Link to="/mot-de-passe-oublie" className="text-xs text-neutral-500 link-underline">Mot de passe oublié ?</Link></div>
        <button disabled={loading} className="btn btn-dark w-full" data-testid="login-submit">{loading ? "…" : "Se connecter"}</button>
      </form>
      <p className="text-sm text-neutral-500 text-center mt-6">Pas encore de compte ? <Link to="/inscription" className="text-[#E0115F] link-underline">Créer un compte</Link></p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(f.name, f.email, f.password); toast.success("Compte créé"); nav("/compte"); }
    catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };
  return (
    <Shell title="Créer un compte" sub="Rejoignez la maison">
      <form onSubmit={submit} className="space-y-4">
        <input required placeholder="Nom complet" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="register-name" />
        <input type="email" required placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="register-email" />
        <input type="password" required minLength={6} placeholder="Mot de passe (6 car. min)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={field} data-testid="register-password" />
        <button disabled={loading} className="btn btn-dark w-full" data-testid="register-submit">{loading ? "…" : "Créer mon compte"}</button>
      </form>
      <p className="text-sm text-neutral-500 text-center mt-6">Déjà inscrit ? <Link to="/connexion" className="text-[#E0115F] link-underline">Se connecter</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { const api = (await import("@/lib/api")).default; await api.post("/auth/forgot-password", { email }); } catch (err) { console.error("Forgot password request failed", err); }
    setSent(true);
  };
  return (
    <Shell title="Mot de passe oublié">
      {sent ? <p className="text-sm text-neutral-600 text-center">Si cet e-mail est enregistré, un lien de réinitialisation vous a été envoyé.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <input type="email" required placeholder="Votre e-mail" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="forgot-email" />
          <button className="btn btn-dark w-full" data-testid="forgot-submit">Envoyer le lien</button>
        </form>
      )}
      <p className="text-sm text-center mt-6"><Link to="/connexion" className="link-underline">Retour à la connexion</Link></p>
    </Shell>
  );
}

export function Reset() {
  const [password, setPassword] = useState("");
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe réinitialisé"); nav("/connexion");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Nouveau mot de passe">
      <form onSubmit={submit} className="space-y-4">
        <input type="password" required minLength={6} placeholder="Nouveau mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} className={field} data-testid="reset-password" />
        <button className="btn btn-dark w-full" data-testid="reset-submit">Réinitialiser</button>
      </form>
      <p className="text-sm text-center mt-6"><Link to="/connexion" className="link-underline">Retour à la connexion</Link></p>
    </Shell>
  );
}
